import { z } from 'zod';

// Create product form data type
export interface CreateProductFormData {
  shopId: string;
  categoryId: string;
  name: string;
  description: string;
  price: number;
  discountPrice?: number;
  quantity: number;
  currency: 'comoros_currency' | 'european_currency' | 'american_currency';
  unit: 'sold_individually' | 'sold_in_groups' | 'grams' | 'kilogram' | 'milliliter' | 'liter';
  gender?: 'men' | 'women' | 'both' | 'none';
  sizes?: string;
  tags?: string;
  weight: number;
  weightUnit: 'grams' | 'kilograms' | 'pounds' | 'ounces';
}

// Update product form data type
export interface UpdateProductFormData {
  categoryId?: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  currency: 'comoros_currency' | 'european_currency' | 'american_currency';
  unit: 'sold_individually' | 'sold_in_groups' | 'grams' | 'kilogram' | 'milliliter' | 'liter';
  gender?: 'men' | 'women' | 'both' | 'none';
  sizes?: string;
  tags?: string;
  weight: number;
  weightUnit: 'grams' | 'kilograms';
}

// Create product form validation schema
export const createProductSchema = z.object({
  shopId: z.string().min(1, 'Shop is required'),
  categoryId: z.string().min(1, 'Category is required'),
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  description: z.string().min(10, 'Description must be at least 10 characters').max(300),
  price: z.number().positive('Price must be positive').max(999999.99),
  discountPrice: z.number().min(0).max(999999.99).optional(),
  quantity: z.number().int().min(0, 'Quantity must be 0 or more').max(999999),
  currency: z.enum(['comoros_currency', 'european_currency', 'american_currency']),
  unit: z.enum(['sold_individually', 'sold_in_groups', 'grams', 'kilogram', 'milliliter', 'liter']),
  gender: z.enum(['men', 'women', 'both', 'none']).optional(),
  sizes: z.string().optional(),
  tags: z.string().optional(),
  weight: z.number().min(0.01, 'Weight is required').max(999999),
  weightUnit: z.enum(['grams', 'kilograms', 'pounds', 'ounces']),
});

// Update product form validation schema (more permissive)
export const updateProductSchema = z.object({
  categoryId: z.string().optional(),
  name: z.string().min(1, 'Name is required').max(150),
  description: z.string().max(300),
  price: z.number().min(0, 'Price must be 0 or more').max(999999.99),
  quantity: z.number().int().min(0, 'Quantity must be 0 or more').max(999999),
  currency: z.enum(['comoros_currency', 'european_currency', 'american_currency']),
  unit: z.enum(['sold_individually', 'sold_in_groups', 'grams', 'kilogram', 'milliliter', 'liter']),
  gender: z.enum(['men', 'women', 'both', 'none']).optional(),
  sizes: z.string().optional(),
  tags: z.string().optional(),
  weight: z.number().min(0.01, 'Weight is required for shipping').max(999999),
  weightUnit: z.enum(['grams', 'kilograms']),
});

// Earnings estimation type
export interface EarningsEstimation {
  price: number;
  commissionRate: number;
  commissionAmount: number;
  netAmount: number;
  currentPlanName?: string;
  betterPlans: Array<{
    name: string;
    commissionRate: number;
    netAmount: number;
    savings: number;
  }>;
}

// Form progress type
export interface FormProgress {
  completedFields: number;
  totalFields: number;
  progress: number;
}
